import React, { useCallback } from 'react';
import {
  Button,
  ChevronRightIcon,
  Chip,
  Fact,
  LedgerHoverCard,
  LedgerRow,
  hideBelowWidthClass,
  showBelowWidthClass,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import type { SiteListItem as SiteListItemType } from '@billda/api';
import { ProgressBar } from '../atoms/ProgressBar';
import { formatCurrencyWhole } from '../../../../../shared/utils/currency';

interface SiteListItemProps {
  site: SiteListItemType;
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  onClick: (siteId: number) => void;
}

const REDUCED_WIDTH = 760;

const formatProgress = (progress: number): string =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(progress);

export const SiteListItem = React.memo(function SiteListItem({
  site,
  columns,
  collapse,
  onClick,
}: SiteListItemProps) {
  const handleClick = useCallback(() => onClick(site.id), [onClick, site.id]);
  const financials = site.financialMetrics;
  const isArchived = site.archivedAt != null;
  const value = financials
    ? formatCurrencyWhole(financials.totalLabourPrice)
    : '—';
  const progress = financials ? formatProgress(financials.progress) : null;
  const plotLabel = `${site.plotCount} plot${site.plotCount === 1 ? '' : 's'}`;

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      title={site.name}
      titleTag={
        isArchived ? (
          <Chip label="Archived" size="sm" data-testid="site-archived-chip" />
        ) : undefined
      }
      cells={{
        plots: plotLabel,
        value,
        progress: financials ? (
          <>
            <div
              className={hideBelowWidthClass(collapse.container, REDUCED_WIDTH)}
            >
              <ProgressBar percentage={financials.progress} />
            </div>
            <span
              className={showBelowWidthClass(collapse.container, REDUCED_WIDTH)}
            >
              {progress}%
            </span>
          </>
        ) : (
          '—'
        ),
      }}
      meta={`${plotLabel} · ${value}${progress !== null ? ` · ${progress}% complete` : ''}`}
      showValues={false}
      hoverCard={
        <LedgerHoverCard
          title={site.name}
          kind="Site"
          onOpenDetails={handleClick}
          openDetailsLabel="Open site"
        >
          {site.what3WordsAddress && (
            <Fact label="Address">{site.what3WordsAddress}</Fact>
          )}
          <Fact label="Plots">{site.plotCount}</Fact>
          <Fact label="Value">{value}</Fact>
          <Fact label="Progress">
            {progress !== null ? `${progress}%` : '—'}
          </Fact>
        </LedgerHoverCard>
      }
      actions={
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          aria-label={`Open ${site.name}`}
          className="size-8"
        >
          <ChevronRightIcon aria-hidden="true" className="size-4" />
        </Button>
      }
      onClick={handleClick}
      dimmed={isArchived}
      aria-label={`Open site ${site.name}`}
      data-testid="site-row"
    />
  );
});
